import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import {
  Module,
  Action,
  Mutation,
  VuexModule,
  config,
} from "vuex-module-decorators";
import { AWSGlobal, Cognito } from "@/aws-cognito/Init";
import CognitoConfig from "@/aws-cognito/config.js";
import AuthenticationHelper from "@/aws-cognito/AuthenticationHelper.js";
import BigInteger from "@/aws-cognito/BigInteger.js";
import router from "@/router/index";
import { UserStatus } from "@/types/enum/UserStatus";

export interface User {
  name: string;
  surname: string;
  first_name: any;
  last_name: any;
  email: string;
  password: string;
  access_token: string;
  refresh_token: string;
  image: string;
  user_id: string;
  fcm_token: string;
  status: number;
  admin_message: string;
  profile_language: string;
}

export interface UserAuthInfo {
  errors: unknown;
  user: User;
  isAuthenticated: boolean;
}

export interface ChallengeParams {
  email: string;
  session: string | null;
  challenge_parameters: any | null;
  challenge_name: string | null;
}

config.rawError = true;

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = {};
  user = {} as User;
  isAuthenticated = !!JwtService.getToken();
  challengeParams = {} as ChallengeParams;

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): User {
    const userInfo = JSON.parse(
      localStorage.getItem("suits_attorney_info") || "{}"
    );
    const user = {
      email: userInfo.username || "",
      access_token: userInfo.access_token || "",
      refresh_token: userInfo.refresh_token || "",
      name: userInfo.name || "",
      first_name: userInfo.first_name || "",
      last_name: userInfo.last_name || "",
      image: userInfo.image || "",
      user_id: userInfo.user_id || "",
      fcm_token: userInfo.fcm_token || "",
      admin_message: userInfo.admin_message || "",
      profile_language: userInfo.profile_language || "",
      status: userInfo.status || UserStatus.INACTIVE,
    } as User;

    return Object.keys(this.user).length ? this.user : user;
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getErrors() {
    return this.errors;
  }

  get getToken(): string {
    return JwtService.getToken() as string;
  }

  get getRefreshToken(): string {
    return JwtService.getRefreshToken() as string;
  }

  get getChallengeParams(): ChallengeParams {
    const challengeParams = JSON.parse(
      localStorage.getItem("attorney_challenge_params") || "{}"
    );
    return Object.keys(this.challengeParams).length
      ? this.challengeParams
      : challengeParams;
  }

  @Mutation
  [Mutations.SET_CHALLENGE_PARAMS](data: ChallengeParams) {
    this.challengeParams = data;
    localStorage.setItem("attorney_challenge_params", JSON.stringify(data));
  }

  @Mutation
  [Mutations.SET_ERROR](error) {
    this.errors = { ...error };
  }

  @Mutation
  [Mutations.SET_AUTH](user) {
    this.isAuthenticated = true;
    this.user = user;
    this.errors = {};
    JwtService.saveToken(JwtService.ACCESS_TOKEN, user.access_token);
    JwtService.saveToken(JwtService.REFRESH_TOKEN, user.refresh_token);
  }

  @Mutation
  [Mutations.SET_USER](user) {
    const userInfo = {
      username: user.email,
      access_token: user.access_token,
      refresh_token: user.refresh_token,
      name: user.name,
      first_name: user.first_name,
      last_name: user.last_name,
      image: user.image,
      user_id: user.user_id,
      fcm_token: user.fcm_token,
      status: user.status,
      admin_message: user.admin_message,
      profile_language: user.profile_language,
    };
    window.localStorage.setItem(
      "suits_attorney_info",
      JSON.stringify(userInfo)
    );

    this.user = user;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_PASSWORD](password) {
    this.user.password = password;
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;
    this.user = {} as User;
    this.errors = [];

    const token = JwtService.getToken();
    JwtService.destroyToken(JwtService.ACCESS_TOKEN);
    JwtService.destroyToken(JwtService.REFRESH_TOKEN);
    localStorage.removeItem("suits_attorney_info");
    router.push({ name: "SignIn" });
  }

  @Action({ rawError: true })
  [Actions.LOGIN](credentials) {
    const secretHash = AWSGlobal.util.crypto.hmac(
      CognitoConfig.poolData.ClientSecretKey,
      credentials.email + CognitoConfig.poolData.ClientId,
      "base64",
      "sha256"
    );

    return new Promise((resolve, reject) => {
      return new Promise((rslv, rejc) => {
        AuthenticationHelper.getLargeAValue((err, aValue) => {
          const SRP_A: string = aValue.toString(16);

          Cognito.initiateAuth({
            ClientId: CognitoConfig.poolData.ClientId,
            AuthParameters: {
              USERNAME: credentials.email,
              SECRET_HASH: secretHash,
              SRP_A,
            },
            AuthFlow: "USER_SRP_AUTH",
          })
            .promise()
            .then((res) => {
              rslv(res);
            })
            .catch((err) => {
              rejc(err);
            });
        });
      })
        .then((response: any) => {
          const dateNow = AuthenticationHelper.getNowString();

          const serverBValue = new BigInteger(
            response.ChallengeParameters.SRP_B,
            16
          );

          const salt = new BigInteger(response.ChallengeParameters.SALT, 16);

          return new Promise((rslv1, rejc1) => {
            AuthenticationHelper.getPasswordAuthenticationKey(
              credentials.email,
              credentials.password,
              serverBValue,
              salt,
              (err, hkdf) => {
                Cognito.respondToAuthChallenge({
                  ClientId: CognitoConfig.poolData.ClientId,
                  ChallengeResponses: {
                    SECRET_HASH: secretHash,
                    PASSWORD_CLAIM_SIGNATURE: AWSGlobal.util.crypto.hmac(
                      hkdf,
                      AWSGlobal.util.buffer.concat([
                        new AWSGlobal.util.Buffer(
                          CognitoConfig.poolData.UserPoolId.split("_")[1],
                          "utf8"
                        ),
                        new AWSGlobal.util.Buffer(credentials.email, "utf8"),
                        new AWSGlobal.util.Buffer(
                          response.ChallengeParameters.SECRET_BLOCK,
                          "base64"
                        ),
                        new AWSGlobal.util.Buffer(dateNow, "utf8"),
                      ]),
                      "base64",
                      "sha256"
                    ),
                    PASSWORD_CLAIM_SECRET_BLOCK:
                      response.ChallengeParameters.SECRET_BLOCK,
                    TIMESTAMP: dateNow,
                    USERNAME: response.ChallengeParameters.USER_ID_FOR_SRP,
                  },
                  ChallengeName: response.ChallengeName,
                })
                  .promise()
                  .then((res) => {
                    rslv1(res);
                  })
                  .catch((error) => {
                    rejc1(error);
                  });
              }
            );
          });
        })
        .then((response: any) => {
          if (response.ChallengeName == "SOFTWARE_TOKEN_MFA") {
            this.context.commit(Mutations.SET_CHALLENGE_PARAMS, {
              email: credentials.email,
              challenge_parameters: response.ChallengeParameters,
              challenge_name: response.ChallengeName,
              session: response?.Session || null,
            });
          } else {
            const user = this.currentUser;
            user.access_token = response.AuthenticationResult.AccessToken;
            user.email = credentials.email;
            user.refresh_token = response.AuthenticationResult.RefreshToken;
            this.context.commit(Mutations.SET_AUTH, user);
          }
          resolve(response);
        })
        .catch((error) => {
          this.context.commit(Mutations.SET_ERROR, [error.message]);
          reject(error);
        });
    });
  }

  @Action
  [Actions.LOGOUT]() {
    return Cognito.globalSignOut({
      AccessToken: JwtService.getToken(),
    })
      .promise()
      .then((response) => {
        this.context.commit(Mutations.PURGE_AUTH);
      })
      .catch((error) => {
        this.context.commit(Mutations.PURGE_AUTH);
      });
  }

  @Action
  [Actions.REGISTER](credentials) {
    const secretHash = AWSGlobal.util.crypto.hmac(
      CognitoConfig.poolData.ClientSecretKey,
      credentials.email + CognitoConfig.poolData.ClientId,
      "base64",
      "sha256"
    );

    return Cognito.signUp({
      ClientId: CognitoConfig.poolData.ClientId,
      Username: credentials.email,
      Password: credentials.password,
      SecretHash: secretHash,
      UserAttributes: [
        {
          Name: "email",
          Value: credentials.email,
        },
        {
          Name: "custom:account_type",
          Value: "attorney",
        },
        {
          Name: "name",
          Value: credentials.profile_name,
        },
        {
          Name: "family_name",
          Value: "",
        },
        {
          Name: "locale",
          Value: credentials.locale,
        },
      ],
    })
      .promise()
      .then(() => {
        const user = this.currentUser;
        user.email = credentials.email;
        this.context.commit(Mutations.SET_USER, user);
      })
      .catch((error) => {
        this.context.commit(Mutations.SET_ERROR, [error.message]);
      });

    // return ApiService.post("register", credentials)
    //   .then(({ data }) => {
    //     this.context.commit(Mutations.SET_AUTH, data);
    //   })
    //   .catch(({ response }) => {
    //     this.context.commit(Mutations.SET_ERROR, response.data.errors);
    //   });
  }

  @Action({ rawError: true })
  [Actions.CONFIRM_SIGNUP](credentials) {
    const user = this.currentUser;

    // update current user.
    if (!user.email) {
      user.email = credentials.email as string;
      this.context.commit(Mutations.SET_USER, user);
    }

    const secretHash = AWSGlobal.util.crypto.hmac(
      CognitoConfig.poolData.ClientSecretKey,
      credentials.email + CognitoConfig.poolData.ClientId,
      "base64",
      "sha256"
    );

    return new Promise((resolve, reject) => {
      Cognito.confirmSignUp({
        ClientId: CognitoConfig.poolData.ClientId,
        Username: credentials.email,
        ConfirmationCode: credentials.verification_code,
        SecretHash: secretHash,
      })
        .promise()
        .then((response) => {
          this.context.commit(Mutations.SET_USER, this.currentUser);
          resolve(response);
        })
        .catch((error) => {
          this.context.commit(Mutations.SET_ERROR, [error.message]);
          reject(error);
        });
    });
  }

  @Action
  [Actions.RESEND_CONFIRMATION_CODE](credentials) {
    const user = this.currentUser;

    // update current user.
    if (!user.email) {
      user.email = window.localStorage.getItem("username") as string;
      this.context.commit(Mutations.SET_USER, user);
    }

    const secretHash = AWSGlobal.util.crypto.hmac(
      CognitoConfig.poolData.ClientSecretKey,
      this.currentUser.email + CognitoConfig.poolData.ClientId,
      "base64",
      "sha256"
    );

    return Cognito.resendConfirmationCode({
      ClientId: CognitoConfig.poolData.ClientId,
      Username: this.currentUser.email,
      SecretHash: secretHash,
    })
      .promise()
      .then((response) => {
        this.context.commit(Mutations.SET_USER, this.currentUser);
      })
      .catch((error) => {
        this.context.commit(Mutations.SET_ERROR, [error.message]);
      });
  }

  @Action({ rawError: true })
  [Actions.REFRESH_TOKEN]() {
    const user = this.currentUser;

    // update current user.
    if (!user.email) {
      user.email = window.localStorage.getItem("username") as string;
      this.context.commit(Mutations.SET_USER, user);
    }

    const secretHash = AWSGlobal.util.crypto.hmac(
      CognitoConfig.poolData.ClientSecretKey,
      this.currentUser.email + CognitoConfig.poolData.ClientId,
      "base64",
      "sha256"
    );

    return new Promise((resolve, reject) => {
      Cognito.initiateAuth({
        ClientId: CognitoConfig.poolData.ClientId,
        AuthParameters: {
          SECRET_HASH: secretHash,
          REFRESH_TOKEN: JwtService.getRefreshToken(),
        },
        AuthFlow: "REFRESH_TOKEN_AUTH",
      })
        .promise()
        .then((response) => {
          const user = this.currentUser;
          user.access_token = response.AuthenticationResult.AccessToken;
          this.context.commit(Mutations.SET_AUTH, user);
          resolve(response);
        })
        .catch((error) => {
          this.context.commit(Mutations.PURGE_AUTH);
          reject(error);
        });
    });
  }

  @Action({ rawError: true })
  [Actions.FORGOT_PASSWORD](credentials) {
    const secretHash = AWSGlobal.util.crypto.hmac(
      CognitoConfig.poolData.ClientSecretKey,
      credentials.email + CognitoConfig.poolData.ClientId,
      "base64",
      "sha256"
    );

    return new Promise((resolve, reject) => {
      Cognito.forgotPassword({
        ClientId: CognitoConfig.poolData.ClientId,
        Username: credentials.email,
        SecretHash: secretHash,
      })
        .promise()
        .then((response) => {
          const user = this.currentUser;
          user.email = credentials.email;
          this.context.commit(Mutations.SET_USER, user);
          resolve(response);
        })
        .catch((error) => {
          this.context.commit(Mutations.SET_ERROR, [error.message]);
          reject(error);
        });
    });
  }

  @Action({ rawError: true })
  [Actions.RESET_PASSWORD](credentials) {
    const user = this.currentUser;

    const secretHash = AWSGlobal.util.crypto.hmac(
      CognitoConfig.poolData.ClientSecretKey,
      user.email + CognitoConfig.poolData.ClientId,
      "base64",
      "sha256"
    );

    return new Promise((resolve, reject) => {
      Cognito.confirmForgotPassword({
        ClientId: CognitoConfig.poolData.ClientId,
        Username: user.email,
        Password: credentials.password,
        ConfirmationCode: credentials.confirmation_code,
        SecretHash: secretHash,
      })
        .promise()
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          this.context.commit(Mutations.SET_ERROR, [error.message]);
          reject(error);
        });
    });
  }

  @Action
  [Actions.MFA_CODE](credentials) {
    const challengeParams = this.getChallengeParams;

    const secretHash = AWSGlobal.util.crypto.hmac(
      CognitoConfig.poolData.ClientSecretKey,
      challengeParams.email + CognitoConfig.poolData.ClientId,
      "base64",
      "sha256"
    );

    return new Promise((resolve, reject) => {
      Cognito.respondToAuthChallenge({
        ClientId: CognitoConfig.poolData.ClientId,
        ChallengeName: "SOFTWARE_TOKEN_MFA",
        Session: challengeParams.session || "",
        ChallengeResponses: {
          SECRET_HASH: secretHash,
          SOFTWARE_TOKEN_MFA_CODE: credentials.mfa_code,
          USERNAME: challengeParams.email || "",
        },
      })
        .promise()
        .then((response) => {
          const user = this.currentUser;
          user.access_token = response.AuthenticationResult.AccessToken;
          user.email = credentials.email;
          user.refresh_token = response.AuthenticationResult.RefreshToken;
          this.context.commit(Mutations.SET_AUTH, user);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  @Action
  [Actions.PURGE_AUTH]() {
    this.context.commit(Mutations.PURGE_AUTH);
    return Promise.resolve();
  }

  @Action
  [Actions.VERIFY_AUTH](payload) {
    if (JwtService.getToken()) {
      // ApiService.setHeader();
      // ApiService.post("verify_token", payload)
      //   .then(({ data }) => {
      //     this.context.commit(Mutations.SET_AUTH, data);
      //   })
      //   .catch(({ response }) => {
      //     this.context.commit(Mutations.SET_ERROR, response.data.errors);
      //     this.context.commit(Mutations.PURGE_AUTH);
      //   });
    } else {
      this.context.commit(Mutations.PURGE_AUTH);
    }
  }

  @Action
  [Actions.SET_USER_ACTION](payload) {
    this.context.commit(Mutations.SET_USER, payload.user);
    return Promise.resolve(payload.data);
  }

  @Action({ rawError: true })
  [Actions.UPDATE_USER_STATUS](payload) {
    const user = this.currentUser;

    user.status = payload;

    this.context.commit(Mutations.SET_USER, user);

    return Promise.resolve(true);
  }
}
